import UnlockContact from '../components/Credits';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar from './EmployerSidebar';

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ResumeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const FunnelIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <polygon points="4 4 20 4 14 12.5 14 19 10 21 10 12.5 4 4" />
  </svg>
);
const BriefcaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);
const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const fileUrl = (p) => (!p ? null : p.startsWith('http') ? p : `${BACKEND_URL}/storage/${p}`);

const formatSalary = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  if (num >= 100000) return `${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)} Lakh`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const formatExperience = (years) => {
  if (years === null || years === undefined || years === '') return null;
  const num = Number(years);
  if (Number.isNaN(num)) return null;
  if (num < 1) return `${Math.round(num * 12)} mos`;
  return `${num} yr${num === 1 ? '' : 's'}`;
};

const formatActiveDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

// Text input with an AI suggestion dropdown.
// multi=true: value is comma separated; suggestions use the text after the last comma.
// transform: optional function applied to the picked suggestion (single mode).
function SuggestInput({ value, onChange, url, multi = false, placeholder, className, transform }) {
  const [suggestions, setSuggestions] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const parts = v.split(',');
    const token = (multi ? parts[parts.length - 1] : v).trim();
    if (token.length < 2) {
      setSuggestions([]);
      setShow(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const body = { query: token };
        if (multi) body.existing_skills = parts.slice(0, -1).map((s) => s.trim()).filter(Boolean);
        const res = await api.post(url, body);
        setSuggestions(res.data?.suggestions || []);
        setShow(true);
      } catch (err) {
        console.error('Suggestion failed', err);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const pick = (s) => {
    if (multi) {
      const parts = value.split(',');
      parts[parts.length - 1] = ` ${s}`;
      onChange(parts.map((p) => p.trim()).filter(Boolean).join(', ') + ', ');
    } else {
      onChange(transform ? transform(s) : s);
    }
    setShow(false);
    setSuggestions([]);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setShow(true); }}
        onBlur={() => setTimeout(() => setShow(false), 150)}
        autoComplete="off"
        placeholder={placeholder}
        className={className + ' pr-8'}
      />
      {loading && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin" />
      )}
      {show && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-purple-100 rounded-xl shadow-lg max-h-48 overflow-y-auto py-1">
          {suggestions.map((s, i) => (
            <li
              key={i}
              onMouseDown={() => pick(s)}
              className="px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 cursor-pointer transition-colors"
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const emptyFilters = {
  search: '', skills: '', location: '', min_experience: '', max_experience: '',
  max_salary: '', gender: '', education: '', languages: '', active_within: '', has_resume: false, saved_only: false, job_id: '',
};

function CandidateCard({ c, onToggleSave }) {
  const MatchBadge = ({ percent }) => {
  if (percent == null) return null;
  const color =
    percent >= 80 ? 'bg-green-100 text-green-700'
    : percent >= 50 ? 'bg-purple-100 text-purple-700'
    : 'bg-orange-100 text-orange-700';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full tabular-nums shrink-0 ${color}`}>
      ✦ {Math.round(percent)}% match
    </span>
  );
};

const MatchBreakdown = ({ breakdown }) => {
  if (!breakdown || breakdown.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2 mb-1">
      <span className="text-xs font-bold text-purple-600 mr-0.5">✦ Matching:</span>
      {breakdown.map((b) => (
        <span
          key={b.key}
          className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${
            b.matched ? 'border-purple-300 text-purple-700 bg-purple-50' : 'border-gray-200 text-gray-400 bg-gray-50'
          }`}
        >
          {b.matched ? '✓' : '✕'} {b.label}
        </span>
      ))}
    </div>
  );
};
  const [expanded, setExpanded] = useState(false);
  const [unlocked, setUnlocked] = useState(null);
  const contactLocked = !!c.contact_locked && !unlocked;
  const phone = unlocked ? unlocked.phone : c.phone;
  const experience = formatExperience(c.experience_years);
  const salary = formatSalary(c.expected_salary);
  const activeDate = formatActiveDate(c.last_active_at);
  const photo = fileUrl(c.profile_photo_path);
  const resume = fileUrl((unlocked && unlocked.resume_path) || c.resume_path);

  const allSkills = c.skills || [];
  const visibleSkills = expanded ? allSkills : allSkills.slice(0, 4);
  const hiddenCount = allSkills.length - visibleSkills.length;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center shrink-0 overflow-hidden">
          {photo ? (
            <img src={photo} alt={c.name} className="w-full h-full object-cover" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
              <circle cx="12" cy="8" r="3.4" />
              <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" />
            </svg>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="font-bold text-lg text-[#0D1B3D]">{c.name || 'Unknown candidate'}</h3>
            <Link to={`/employer/candidates/${c.id}`} className="text-xs font-semibold text-purple-600 hover:text-pink-600 hover:underline whitespace-nowrap">
              View Full Profile »
            </Link>
            <div className="ml-auto flex items-center gap-2">
              <MatchBadge percent={c.match_percentage} />
              <button
                type="button"
                onClick={() => onToggleSave(c)}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-4 py-1.5 rounded-full border transition-all active:scale-95 ${
                  c.saved
                    ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white border-transparent shadow-sm'
                    : 'border-purple-200 text-purple-600 hover:bg-purple-50'
                }`}
              >
                {c.saved ? '★ Saved' : '☆ Save'}
              </button>
            </div>
          </div>
            
          <MatchBreakdown breakdown={c.match_breakdown} />
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            {experience && <span className="inline-flex items-center gap-1"><BriefcaseIcon /> {experience}</span>}
            {salary && <span className="inline-flex items-center gap-1">💰 {salary}</span>}
            {c.city && <span className="inline-flex items-center gap-1"><PinIcon /> {c.city}</span>}
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
            {(c.current_designation || c.current_company) && (
              <>
                <span className="text-gray-400 shrink-0">Current / Latest</span>
                <span className="font-medium text-[#0D1B3D] bg-purple-50 px-2 py-0.5 rounded-full w-fit">
                  {c.current_designation}{c.current_designation && c.current_company ? ' at ' : ''}{c.current_company}
                </span>
              </>
            )}
            {c.educations?.length > 0 && (
              <>
                <span className="text-gray-400 shrink-0">Education</span>
                <span className="text-gray-700">{c.educations.map((e) => e.degree).filter(Boolean).join(', ')}</span>
              </>
            )}
            {c.preferred_location && (
              <>
                <span className="text-gray-400 shrink-0">Pref. Location</span>
                <span className="text-gray-700">{c.preferred_location}</span>
              </>
            )}
            {allSkills.length > 0 && (
              <>
                <span className="text-gray-400 shrink-0">Skills</span>
                <span className="flex flex-wrap items-center gap-1.5">
                  {visibleSkills.map((s) => (
                    <span key={s.name} className={`text-xs px-2 py-0.5 rounded-full ${s.matched ? 'bg-purple-100 text-purple-700 font-semibold' : 'bg-gray-100 text-gray-600'}`}>
                      {s.name}
                    </span>
                  ))}
                  {hiddenCount > 0 && (
                    <button onClick={() => setExpanded(true)} className="text-xs font-semibold text-[#2563EB] hover:underline">
                      +{hiddenCount} more
                    </button>
                  )}
                </span>
              </>
            )}
            {c.languages?.length > 0 && (
              <>
                <span className="text-gray-400 shrink-0">Languages</span>
                <span className="text-gray-700">
                  {c.languages.map((l) => `${l.name}${l.proficiency ? ` (${l.proficiency})` : ''}`).join(' | ')}
                </span>
              </>
            )}
          </div>

          {(phone || contactLocked || resume) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {contactLocked && (
                <UnlockContact candidateId={c.id} onUnlocked={setUnlocked} />
              )}
              {phone && (
                <a href={`tel:${phone}`} className="inline-flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-semibold px-5 py-2 rounded-full shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 active:scale-95">
                  <PhoneIcon /> {phone}
                </a>
              )}
              {resume && (
                <a href={resume} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-white border border-purple-300 text-purple-600 hover:bg-purple-50 text-sm font-semibold px-5 py-2 rounded-full transition-all active:scale-95">
                  <ResumeIcon /> View Resume
                </a>
              )}
            </div>
          )}

          {activeDate && (
            <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">Active on {activeDate}</div>
          )}
        </div>
      </div>
    </div>
  );
}

const STORAGE_KEY = 'employerDatabaseState';

const loadSavedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const s = raw ? JSON.parse(raw) : null;
    const jobFromUrl = new URLSearchParams(window.location.search).get('job');
    const filters = { ...emptyFilters, ...(s?.filters || {}) };
    const applied = { ...emptyFilters, ...(s?.applied || {}) };
    if (jobFromUrl) {            // ?job= in the URL wins over the saved value
      filters.job_id = jobFromUrl;
      applied.job_id = jobFromUrl;
    }
    return { filters, applied, page: s?.page || 1 };
  } catch {
    return { filters: emptyFilters, applied: emptyFilters, page: 1 };
  }
};

export default function EmployerDatabase() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [candidates, setCandidates] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [initial] = useState(loadSavedState);
const [filters, setFilters] = useState(initial.filters);   // what the inputs show
const [applied, setApplied] = useState(initial.applied);   // what was sent to the API
const [page, setPage] = useState(initial.page);
    const [savedCount, setSavedCount] = useState(0);
      const [myJobs, setMyJobs] = useState([]);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  useEffect(() => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ filters, applied, page }));
}, [filters, applied, page]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);
    setError('');
    const params = { page };
    Object.entries(applied).forEach(([k, v]) => {
      if (v !== '' && v !== false) params[k] = v;
    });
    api.get('/employer/candidates', { params })
      .then((res) => {
        setCandidates(res.data.data || []);
        setMeta({ current_page: res.data.current_page, last_page: res.data.last_page, total: res.data.total });
        setSavedCount(res.data.saved_count ?? 0);
      })
      .catch((err) => setError(err.response?.data?.message || "Couldn't load candidates."))
      .finally(() => setLoading(false));
  }, [applied, page, navigate]);

    useEffect(() => {
    api.get('/jobs')
      .then((res) => setMyJobs(Array.isArray(res.data) ? res.data : (res.data?.data ?? [])))
      .catch(() => {});
  }, []);


  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const applyFilters = () => { setPage(1); setApplied(filters); setShowFiltersMobile(false); };
  const resetFilters = () => { setFilters(emptyFilters); setApplied(emptyFilters); setPage(1); };
    const showTab = (savedOnly) => {
    setFilters((f) => ({ ...f, saved_only: savedOnly }));
    setApplied((a) => ({ ...a, saved_only: savedOnly }));
    setPage(1);
  };
  const toggleSave = async (c) => {
    const next = !c.saved;
    const removeFromView = applied.saved_only && !next;

    setCandidates((list) =>
      removeFromView ? list.filter((x) => x.id !== c.id) : list.map((x) => (x.id === c.id ? { ...x, saved: next } : x))
    );
    setSavedCount((n) => Math.max(0, n + (next ? 1 : -1)));
    if (removeFromView) setMeta((m) => ({ ...m, total: Math.max(0, m.total - 1) }));

    try {
      if (next) await api.post(`/employer/candidates/${c.id}/save`);
      else await api.delete(`/employer/candidates/${c.id}/save`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update saved status.');
      setApplied((a) => ({ ...a })); // reload the list to undo
    }
  };

  const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm';
  const labelCls = 'text-xs font-semibold text-gray-500 block mb-1';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      <Navbar user={user} />
      <EmployerSidebar user={user} activeTab="database" />

      <div className="lg:pl-16 lg:peer-hover:pl-64 transition-all duration-200 ease-in-out">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <EmployerSidebar user={user} activeTab="database" mobile />

                    <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent tracking-tight mb-1">Candidate Database</h1>
          <p className="text-sm text-gray-500 mb-4">{meta.total} candidate{meta.total === 1 ? '' : 's'} on PlanB Jobs</p>

          {error && <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}

          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => showTab(false)}
              className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all ${
                !applied.saved_only ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm' : 'bg-white text-purple-600 border-purple-200 hover:border-purple-400'
              }`}
            >
              All Candidates
            </button>
            <button
              type="button"
              onClick={() => showTab(true)}
              className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all ${
                applied.saved_only ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white border-transparent shadow-sm' : 'bg-white text-purple-600 border-purple-200 hover:border-purple-400'
              }`}
            >
              ★ Saved Candidates ({savedCount})
            </button>
          </div>

          <button
            onClick={() => setShowFiltersMobile((s) => !s)}
            className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#0D1B3D]"
          >
            <span className="inline-flex items-center gap-2"><FunnelIcon /> Filters</span>
            <span className="text-sm text-gray-400">{showFiltersMobile ? 'Hide ▲' : 'Show ▼'}</span>
          </button>

          <div className="flex flex-col lg:flex-row gap-6 items-start">
            {/* Filters sidebar */}
            <div className={`${showFiltersMobile ? 'block' : 'hidden'} lg:block w-full lg:w-72 shrink-0 lg:sticky lg:top-8 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto`}>
              <div className="bg-white rounded-xl shadow p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm inline-flex items-center gap-1.5 text-purple-700"><FunnelIcon /> Filters</h3>
                  <button onClick={resetFilters} className="text-xs font-semibold text-purple-600 hover:text-pink-600 hover:underline">Reset</button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Match against job</label>
                    <select
                      value={filters.job_id}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilters((f) => ({ ...f, job_id: v }));
                        setApplied((a) => ({ ...a, job_id: v }));
                        setPage(1);
                      }}
                      className={inputCls}
                    >
                      <option value="">No job (no match score)</option>
                      {myJobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Name</label>

                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><SearchIcon /></span>
                      <input type="text" value={filters.search} onChange={(e) => set({ search: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && applyFilters()} placeholder="Search"
                        className="w-full border border-purple-200 rounded-full pl-9 pr-4 py-2 text-sm bg-white hover:border-purple-400 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Skills (comma separated)</label>
                    <SuggestInput value={filters.skills} onChange={(v) => set({ skills: v })} url="/ai/suggest-skills" multi placeholder="e.g. PHP, Laravel" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Location / City</label>
                    <SuggestInput value={filters.location} onChange={(v) => set({ location: v })} url="/ai/suggest-cities" transform={(s) => s.split(',')[0].trim()} placeholder="e.g. Bengaluru" className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls}>Min exp. (yrs)</label>
                      <input type="number" value={filters.min_experience} onChange={(e) => set({ min_experience: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Max exp. (yrs)</label>
                      <input type="number" value={filters.max_experience} onChange={(e) => set({ max_experience: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Max expected salary (₹/yr)</label>
                    <input type="number" value={filters.max_salary} onChange={(e) => set({ max_salary: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Gender</label>
                    <select value={filters.gender} onChange={(e) => set({ gender: e.target.value })} className={inputCls}>
                      <option value="">Any</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Education (e.g. 12th, B.Com)</label>
                    <SuggestInput value={filters.education} onChange={(v) => set({ education: v })} url="/ai/suggest-education" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Languages (comma separated)</label>
                    <SuggestInput value={filters.languages} onChange={(v) => set({ languages: v })} url="/ai/suggest-languages" multi placeholder="e.g. Kannada, Hindi" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Last active</label>
                    <select value={filters.active_within} onChange={(e) => set({ active_within: e.target.value })} className={inputCls}>
                      <option value="">Any time</option>
                      <option value="7">Last 7 days</option>
                      <option value="30">Last 30 days</option>
                      <option value="90">Last 90 days</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={filters.has_resume} onChange={(e) => set({ has_resume: e.target.checked })} className="accent-purple-600" />
                    Has resume
                  </label>

                                    {/* <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={filters.saved_only} onChange={(e) => set({ saved_only: e.target.checked })} className="accent-[#2563EB]" />
                    Saved candidates only
                  </label> */}

                  <button onClick={applyFilters} className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold px-4 py-2.5 rounded-full text-sm shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:brightness-110 active:scale-95">
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Candidate list */}
            <div className="flex-1 w-full space-y-4 min-w-0">
              {loading ? (
                <p className="text-gray-500">Loading candidates...</p>
              ) : candidates.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-10 text-center text-gray-500">
                  {applied.saved_only && savedCount === 0
                    ? "You haven't saved any candidates yet."
                    : 'No candidates match these filters. Try widening them.'}
                </div>
              ) : (
                candidates.map((c) => <CandidateCard key={c.id} c={c} onToggleSave={toggleSave} />)
              )}

              {meta.last_page > 1 && (
                <div className="flex items-center justify-center gap-3 pt-2 text-sm text-gray-600">
                  <button disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}
                    className="px-4 py-1.5 rounded-full border border-purple-200 bg-white text-purple-600 hover:bg-purple-50 disabled:opacity-40">← Prev</button>
                  <span>Page {meta.current_page} of {meta.last_page}</span>
                  <button disabled={page >= meta.last_page || loading} onClick={() => setPage((p) => p + 1)}
className="px-4 py-1.5 rounded-full border border-purple-200 bg-white text-purple-600 hover:bg-purple-50 disabled:opacity-40">Next →</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}